import React, { useState, useEffect } from 'react';
import { 
  WalletIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import CreditCoinsModal from './CreditCoinsModal';
import DebitCoinsModal from './DebitCoinsModal';

const WalletManagement = () => {
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showDebitModal, setShowDebitModal] = useState(false);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      // This would be an endpoint to get all wallets with summary
      const response = await fetch('/api/buildcoins/wallets/summary', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setWallets(data.data);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletDetails = async (consumerId) => {
    try {
      const response = await fetch(`/api/buildcoins/wallets/${consumerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedWallet(data.data);
      }
    } catch (error) {
      console.error('Error fetching wallet details:', error);
    }
  };

  const handleCreditCoins = async (creditData) => {
    try {
      const response = await fetch('/api/buildcoins/wallets/credit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(creditData)
      });
      
      const data = await response.json();
      if (data.success) {
        setShowCreditModal(false);
        fetchWallets();
        if (selectedWallet) {
          fetchWalletDetails(selectedWallet.consumer_id._id);
        }
      }
    } catch (error) {
      console.error('Error crediting coins:', error);
    }
  };

  const handleDebitCoins = async (debitData) => {
    try {
      const response = await fetch('/api/buildcoins/wallets/debit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(debitData)
      });

      const data = await response.json();
      if (data.success) {
        setShowDebitModal(false);
        fetchWallets();
        if (selectedWallet) {
          fetchWalletDetails(selectedWallet.consumer_id._id);
        }
      }
    } catch (error) {
      console.error('Error debiting coins:', error);
    }
  };

  const filteredWallets = wallets.filter(wallet =>
    wallet.consumer_id?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wallet.consumer_id?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wallet Management</h1>
          <p className="text-gray-600">Manage customer BuildCoins wallets</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreditModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <ArrowUpIcon className="w-5 h-5" />
            Credit Coins
          </button>
          <button
            onClick={() => setShowDebitModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <ArrowDownIcon className="w-5 h-5" />
            Debit Coins
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by customer name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Wallet List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Customer Wallets</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredWallets.map((wallet) => (
                <div
                  key={wallet._id}
                  className={`p-6 cursor-pointer hover:bg-gray-50 ${
                    selectedWallet?._id === wallet._id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => fetchWalletDetails(wallet.consumer_id._id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <WalletIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-sm font-medium text-gray-900">
                          {wallet.consumer_id?.customer_name || 'Unknown Customer'}
                        </h3>
                        <p className="text-sm text-gray-500">{wallet.consumer_id?.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {wallet.current_balance.toLocaleString()} coins
                      </p>
                      <p className="text-xs text-gray-500">
                        Available: {wallet.available_balance.toLocaleString()}
                      </p>
                      {wallet.locked_balance > 0 && (
                        <p className="text-xs text-red-500">
                          Locked: {wallet.locked_balance.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Wallet Status Indicators */}
                  <div className="mt-3 flex items-center gap-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      wallet.wallet_status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {wallet.wallet_status}
                    </span>
                    
                    {wallet.coins_expiring_soon > 0 && (
                      <div className="flex items-center text-yellow-600">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        <span className="text-xs">
                          {wallet.coins_expiring_soon} expiring soon
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Wallet Details */}
        <div className="lg:col-span-1">
          {selectedWallet ? (
            <WalletDetailsPanel 
              wallet={selectedWallet} 
              onRefresh={() => fetchWalletDetails(selectedWallet.consumer_id._id)}
            />
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center text-gray-500">
                <WalletIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select a wallet to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Credit Modal */}
      {showCreditModal && (
        <CreditCoinsModal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
          onSubmit={handleCreditCoins}
        />
      )}

      {/* Debit Modal */}
      {showDebitModal && (
        <DebitCoinsModal
          isOpen={showDebitModal}
          onClose={() => setShowDebitModal(false)}
          onSubmit={handleDebitCoins}
        />
      )}
    </div>
  );
};

// Wallet Details Panel Component
const WalletDetailsPanel = ({ wallet, onRefresh }) => {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Wallet Details</h3>
      </div>
      
      <div className="p-6">
        {/* Customer Info */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Customer Information</h4>
          <div className="space-y-2">
            <p className="text-sm text-gray-900">{wallet.consumer_id.customer_name}</p>
            <p className="text-sm text-gray-500">{wallet.consumer_id.email}</p>
            <p className="text-sm text-gray-500">{wallet.consumer_id.mobile_number}</p>
          </div>
        </div>

        {/* Balance Summary */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Balance Summary</h4>
          <div className="space-y-3">
                       <div className="flex justify-between">
              <span className="text-sm text-gray-600">Current Balance:</span>
              <span className="text-sm font-medium text-gray-900">
                {wallet.current_balance.toLocaleString()} coins
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Available Balance:</span>
              <span className="text-sm font-medium text-green-600">
                {wallet.available_balance.toLocaleString()} coins
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Locked Balance:</span>
              <span className="text-sm font-medium text-red-600">
                {wallet.locked_balance.toLocaleString()} coins
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Earned:</span>
              <span className="text-sm font-medium text-blue-600">
                {wallet.total_earned.toLocaleString()} coins
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Spent:</span>
              <span className="text-sm font-medium text-gray-600">
                {wallet.total_spent.toLocaleString()} coins
              </span>
            </div>
          </div>
        </div>

        {/* Expiring Coins */}
        {wallet.expiring_coins && wallet.expiring_coins.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <ClockIcon className="w-4 h-4 mr-2 text-yellow-500" />
              Expiring Soon
            </h4>
            <div className="space-y-2">
              {wallet.expiring_coins.slice(0, 3).map((coin, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {new Date(coin.expiry_date).toLocaleDateString()}
                  </span>
                  <span className="text-yellow-600 font-medium">
                    {coin.coins_amount.toLocaleString()} coins
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Transactions</h4>
          <div className="space-y-3">
            {wallet.recent_transactions?.slice(0, 5).map((transaction) => (
              <div key={transaction._id} className="flex items-center justify-between">
                <div className="flex items-center">
                  {transaction.transaction_type === 'credit' ? (
                    <ArrowUpIcon className="w-4 h-4 text-green-500 mr-2" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 text-red-500 mr-2" />
                  )}
                  <div>
                    <p className="text-sm text-gray-900">{transaction.transaction_category}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-medium ${
                  transaction.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {transaction.transaction_type === 'credit' ? '+' : '-'}
                  {transaction.coins_amount.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
          >
            Refresh
          </button>
          <button className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm">
            View All
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletManagement;

